import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

class SearchPageInputComponent extends Component {
  requiredRefs = [
    'searchPageInput',
    'predictiveResults',
    'predictiveResultsInner',
  ];

  connectedCallback() {
    super.connectedCallback();

    this._handleOutsideClick = (event) => {
      if (!this.contains(event.target)) {
        this.closePredictiveResults();
      }
    };

    document.addEventListener('click', this._handleOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleOutsideClick);
    super.disconnectedCallback();
  }

  handleInput = debounce(async () => {
    const input = this.refs.searchPageInput;
    const query = input.value.trim();

    if (query.length < 1) {
      this.closePredictiveResults();
      return;
    }

    await this.fetchPredictiveResults(query);
  }, 180);

  handleKeyDown = (event) => {
    const input = this.refs.searchPageInput;
    const query = input.value.trim();

    if (event.key === 'Escape') {
      if (!this.refs.predictiveResults.hidden) {
        event.preventDefault();
        this.closePredictiveResults();
        input.focus();
        return;
      }

      if (query === '') {
        this.submitEmptySearch();
      }
    }

    if (event.key === 'ArrowDown') {
      const firstResult =
        this.refs.predictiveResultsInner.querySelector('.ob-predictive-item');

      if (firstResult && !this.refs.predictiveResults.hidden) {
        event.preventDefault();
        firstResult.focus();
      }
    }
  };

  async fetchPredictiveResults(query) {
    try {
      const url = new URL('/search/suggest.json', window.location.origin);

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set(
        'resources[options][unavailable_products]',
        'last'
      );

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Predictive search request failed');
      }

      const data = await response.json();
      const products = data?.resources?.results?.products ?? [];

      if (this.refs.searchPageInput.value.trim() !== query) {
        return;
      }

      this.renderProducts(products, query);
    } catch (error) {
      console.error('OVER BROS predictive search:', error);
      this.closePredictiveResults();
    }
  }

  renderProducts(products, query) {
    const container = this.refs.predictiveResultsInner;

    if (!products.length) {
      container.innerHTML = `
        <div class="ob-predictive-empty">
          No products found for “${this.escapeHTML(query)}”
        </div>
      `;

      this.openPredictiveResults();
      return;
    }

    container.innerHTML = products
      .map((product) => {
        const image =
          product.featured_image?.url ||
          product.image ||
          '';

        const imageMarkup = image
          ? `
            <img
              class="ob-predictive-item__image"
              src="${this.escapeHTML(image)}"
              alt="${this.escapeHTML(product.title)}"
              loading="lazy"
            >
          `
          : `
            <div class="ob-predictive-item__image"></div>
          `;

        const price = this.formatPrice(product.price);

        return `
          <a
            class="ob-predictive-item"
            href="${this.escapeHTML(product.url)}"
          >
            ${imageMarkup}

            <span class="ob-predictive-item__title">
              ${this.escapeHTML(product.title)}
            </span>

            <span class="ob-predictive-item__price">
              ${price}
            </span>
          </a>
        `;
      })
      .join('');

    this.openPredictiveResults();
  }

  openPredictiveResults() {
    this.refs.predictiveResults.hidden = false;

    this.refs.searchPageInput.setAttribute(
      'aria-expanded',
      'true'
    );
  }

  closePredictiveResults() {
    this.refs.predictiveResults.hidden = true;

    this.refs.searchPageInput.setAttribute(
      'aria-expanded',
      'false'
    );

    this.refs.predictiveResultsInner.innerHTML = '';
  }

  submitEmptySearch() {
    const input = this.refs.searchPageInput;

    input.focus();
    input.value = '';

    if (this.isEmptyState()) return;

    input.form?.submit();
  }

  isEmptyState() {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  }

  formatPrice(price) {
    if (price === undefined || price === null || price === '') {
      return '';
    }

    const numericPrice = Number(price);

    if (Number.isNaN(numericPrice)) {
      return this.escapeHTML(String(price));
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericPrice);
  }

  escapeHTML(value) {
    const element = document.createElement('div');
    element.textContent = String(value ?? '');

    return element.innerHTML;
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define(
    'search-page-input-component',
    SearchPageInputComponent
  );
}